import _ from 'underscore'
import Backbone from 'backbone'
import Nested from 'backbone-nested-models'
import { LocalStorage } from 'backbone.localstorage'
import Request from '../helpers/request'
import PurchasedList from '../collections/purchase'
import config from '../config'
import ResultList from '../collections/results'
import ClassModel from './class'
import SchoolModel from './school'
import TypeModel from './type'
import RoleModel from './role'
import EmailLimitModel from './email-limit'

const UserModel = Backbone.Model.extend({
    localStorage: new LocalStorage('user-backbone'),
    relations: {
        results: ResultList,
        purchased: PurchasedList,
        class: ClassModel,
        school: SchoolModel,
        type: TypeModel,
        role: RoleModel,
        email_limit: EmailLimitModel,
    },
    defaults: {
        id: 0,
        uid: null,
        username: null,
        email: null,
        firstname: null,
        lastname: null,
        school: {},
        type: {},
        role: {},
        class: {},
        results: [],
        purchased: [],
        loading: false,
        email_limit: {},
        login_token: null,
        login_status: null,
        requirePasswordReset: false
    },
    initialize() {
        // TODO make policy and role model and have relations in this model

        // if it is not the logged in user add the id to the uid
        // the id needs to be 0 for the logged in user to retrieve it from
        // localStorage so uid is needed to store the sever id
        if (this.get('id') !== 0) {
            this.set('uid', this.get('id'))
        }

        // if its the logged in user set locally saved data
        if (this.get('id') === 0) {
            this.fetch()
        }
    },
    login(username, password) {
        // clears any guest results
        this.reset()

        // this will show a loading animation in the relevant areas
        this.set('loading', true)

        // if the user is not logged in log them in
        if (this.get('uid') === null) {
            return Request({
                url: '/api/login',
                method: 'POST',
                data: {
                    username: username,
                    password: password,
                    appId: config.get('id'),
                }
            })
                .then((response) => new Promise((resolve) => {
                    // set the user details
                    this.set({
                        uid: response.user._id,
                        firstname: response.user.firstname,
                        lastname: response.user.lastname,
                        username: response.user.username,
                        purchased: response.user.appIds,
                        loading: false,
                        login_token: response.token,
                    })

                    // set the email - students dont have email
                    if (response.user.email) {
                        this.set('email', response.user.email)
                    }

                    // set the school details
                    if (response.user.school) {
                        this.get('school').set({
                            id: response.user.school._id,
                            name: response.user.school.name,
                            pin: response.user.school.pin,
                        })
                    }

                    // set the type
                    this.get('type').set('name', response.user.type)

                    // set the role
                    if (response.user.role) {
                        this.get('role').set('name', response.user.role)
                    }

                    // set password requires reset
                    if (response.user.requirePasswordReset) {
                        this.set('requirePasswordReset', response.user.requirePasswordReset)
                    }

                    this.save()

                    resolve()
                }))
        }
    },
    logout(skip_menu_render) {
        // logout to invalidate the login token
        const promise = Request({
            url: '/api/logout',
            method: 'POST',
            data: {}
        })

        // reset user data
        this.reset()

        // navigate to the menu
        if (!skip_menu_render) {
            Backbone.history.navigate('', {trigger: true})
        }

        return promise
    },
    update(attributes) {
        if (attributes.firstname === this.get('firstname')) delete attributes.firstname
        if (attributes.lastname === this.get('lastname')) delete attributes.lastname
        if (attributes.username === this.get('username')) delete attributes.username
        if (attributes.email && attributes.email === this.get('email')) delete attributes.email

        if (_.isEmpty(attributes)) {
            return new Promise((resolve, reject) => reject())
        }

        return Request({
            url: '/api/me/',
            method: 'PUT',
            data: attributes
        })
            .then(() => {
                // local save
                this.set(attributes)
                return this.localSave()
            })
    },
    updateTeacher(attributes) {
        if (attributes.firstname === this.get('firstname')) delete attributes.firstname
        if (attributes.lastname === this.get('lastname')) delete attributes.lastname
        if (attributes.username === this.get('username')) delete attributes.username
        if (attributes.email === this.get('email')) delete attributes.email
        if (attributes.role === this.get('role').get('name')) delete attributes.role

        if (_.isEmpty(attributes)) {
            return new Promise((resolve, reject) => reject())
        }

        return Request({
            url: '/api/school/teacher/' + this.get('id'),
            method: 'PUT',
            data: attributes
        })
            .then(() => {
                // local save
                this.set(attributes)
                return this.localSave()
            })
    },
    updateStudent(attributes) {
        if (attributes.firstname === this.get('firstname')) delete attributes.firstname
        if (attributes.lastname === this.get('lastname')) delete attributes.lastname
        if (attributes.username === this.get('username')) delete attributes.username
        if (attributes.classId === this.get('class').get('id')) delete attributes.classId

        if (_.isEmpty(attributes)) {
            return new Promise((resolve, reject) => reject())
        }

        return Request({
            url: '/api/school/student/' + this.get('id'),
            method: 'PUT',
            data: attributes
        })
            .then(() => {
                // local save
                this.set(attributes)
                return this.localSave()
            })
    },
    create(attributes) {
        let api = ''

        // For the logged in user:
        // - this will show a loading animation in the relevant areas
        // - this set the login token
        // - change the use the register api instead of the create api
        //   the register api create a cookie for authenticating future calls
        if (this.get('id') === 0) {
            this.set('loading', true)
            switch(attributes.type) {
                case 'individual':
                    api = '/api/register/individual'
                    break
                case 'student':
                    api = '/api/register/student'
                    break
                case 'teacher':
                    api = '/api/register/teacher'
                    break
            }
        } else {
            switch(attributes.type) {
                case 'student':
                    api = '/api/school/student'
                    break
                case 'teacher':
                    api = '/api/school/teacher'
                    break
            }
        }

        return Request({
            url: api,
            method: 'POST',
            data: attributes,
        })
            .then((response) => {
                // only need to do this for logged in users
                if (this.get('id') === 0) {
                    this.set({
                        uid: response.user._id,
                        firstname: response.user.firstname,
                        lastname: response.user.lastname,
                        username: response.user.username,
                        loading: false,
                    })

                    // set the email - students dont have email
                    if (response.user.email) {
                        this.set('email', response.user.email)
                    }

                    // set the type
                    this.get('type').set('name', response.user.type)

                    // set the role
                    if (response.user.role) {
                        this.get('role').set('name', response.user.role)
                    }

                    // - set the login token
                    // - start the login check
                    this.set('login_token', response.token)
                    this.localSave()
                }
            })
    },
    updatePassword(password) {
        let api = '/api/me/password'

        if (this.get('id') !== 0) {
            switch(this.get('type').get('name')) {
                case 'student':
                    api = '/api/school/student/' + this.get('id') + '/password'
                    break
                case 'teacher':
                    api = '/api/school/teacher/' + this.get('id') + '/password'
                    break
            }
        }

        return Request({
            url: api,
            method: 'POST',
            data: {
                password: password,
            },
        })
    },
    removeSchool() {
        if (this.get('type').get('name') === 'teacher') {
            return Request({
                url: '/api/school/teacher/' + this.get('id'),
                method: 'PUT',
                data: {
                    schoolId: 'delete'
                }
            })
        }
    },
    delete() {
        let api = '/api/me'

        if (this.get('id') !== 0) {
            switch(this.get('type').get('name')) {
                case 'student':
                    api = '/api/school/student/' + this.get('id')
                    break
                case 'teacher':
                    api = '/api/school/teacher/' + this.get('id')
                    break
            }
        }

        return Request({
            url: api,
            method: 'DELETE',
            data: {},
        })
    },
    reset() {
        // clear localStorage
        this.destroy()

        // empty the model
        this.set(this.defaults)
        // empty the _previousAttributes
        this.set(this.defaults)
        // clear the rest
        this.get('email_limit').clear().set(this.get('email_limit').defaults)
        this.get('role').clear().set(this.get('role').defaults)
        this.get('type').clear().set(this.get('type').defaults)
        this.get('school').clear().set(this.get('school').defaults)
    },
    localSave() {
        return new Promise((resolve) => {
            if (this.get('id') === 0 /*&& this.get('uid') !== null*/) {
                // if the logged in user update
                this.save()
            }
            resolve()
        })
    }
})

export default UserModel
